/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import {
  FeaturesInfo,
  AllowedAuthTypes,
  IInitService,
  PendoConfig,
  CasActivitySettings,
  CheckmateSelectionParams,
  StudiesResponse,
  SitesResponse,
  EnvironmentsResponse,
  AppStatusResponse,
  Constants
} from '../types/init'
import * as authService from './auth'
import { toast } from './toast'
import * as settingsApi from './api/api.settings'
import * as etmfApi from './api/etmf'
import * as checkmateService from './checkmate'
import { serviceNavigate } from './navigation'
import { authApiService } from './api/auth'
import { appsApiService } from './api/api.apps'
import i18n from './i18n/i18n'

// Import baseApiService for logging base URL usage
import { baseApiService } from './api/base'

// Import constants from the main constants file
import constants from '../constants/constants'

/**
 * Translation service wrapper for i18next
 * Provides compatibility layer for legacy translation patterns
 */
const translateService = {
  t: (key: string, params?: any): string => {
    const result = i18n.t(key, params)
    return typeof result === 'string' ? result : String(result)
  },
  onReady: (callback: () => void): void => {
    if (i18n.isInitialized) {
      callback()
    } else {
      i18n.on('initialized', callback)
    }
  }
}

/**
 * InitService - React TypeScript equivalent of AngularJS InitService
 *
 * Provides initialization functionality including:
 * - Feature flags loading
 * - Authentication validation
 * - Pendo analytics initialization
 * - CAS activity tracking
 * - Checkmate integration
 */
class InitService implements IInitService {
  private static instance: InitService
  private featuresInfo: FeaturesInfo = {
    etmfFeature: {},
    eosFeature: {},
    rcrFeature: {},
    allFeaturesData: {}
  }
  private allowedAuthTypes: AllowedAuthTypes = {
    allowedAuthTypes: []
  }
  private featuresInfoPromise: Promise<FeaturesInfo> | null = null
  private allowedAuthTypesPromise: Promise<AllowedAuthTypes> | null = null
  private checkmatePromise: Promise<boolean> | null = null

  private constructor() {}

  public static getInstance(): InitService {
    if (!InitService.instance) {
      InitService.instance = new InitService()
    }
    return InitService.instance
  }

  /**
   * Initialize Pendo analytics
   */
  public initPendo(): void {
    if (window.pendo && !checkmateService.isCheckmated()) {
      try {
        const user = authService.getUser()
        const userId = authService.getUserId()

        const config: PendoConfig = userId
          ? {
              visitor: {
                id: userId,
                email: user?.email || '',
                role_name: user?.role || ''
              },
              account: {
                id: userId
              }
            }
          : {}

        window.pendo.initialize(config)
      } catch (e) {
        console.log('Pendo initialize failed', e)
      }
    }
  }

  /**
   * Initialize CAS activity tracker
   */
  public initCasActivityTracker(): void {
    if (window.trackActivity && window.trackActivity.fromCookies) {
      const settings: CasActivitySettings = window.trackActivity.fromCookies({
        ticketCookie: 'CAS-ST',
        trackerUrlCookie: 'CAS-TrackerUrl',
        updateIntervalSecondsCookie: 'CAS-UpdateInterval'
      })

      if (settings.ticket && settings.trackerUrl && settings.updateIntervalSeconds) {
        window.tracker = window.trackActivity(settings)
      }
    }
  }

  /**
   * Redirect user to single site/study when in Checkmate mode
   */
  public async redirectUserToOnlySiteStudyWhenCheckmated(): Promise<void> {
    if (!checkmateService.isCheckmated()) {
      return
    }

    try {
      await this.waitForCheckmate()

      // Get current user data
      const user = authService.getUser()
      if (!user) return

      const userRole = user.role

      if (userRole === constants.user.roles.ETMF_MANAGER) {
        console.log('### getting my studies')
        const data: StudiesResponse = await etmfApi.getMyStudies({ pageSize: 2 })
        const myStudies = data.elements

        if (myStudies.length === 1) {
          console.log('### go directly to study')
          serviceNavigate(`/browser/${myStudies[0].fsiId}`)
        }
      } else if (userRole === constants.user.roles.ETMF_SITE_USER) {
        console.log('### getting my sites')
        const data: SitesResponse = await etmfApi.getMySites({ pageSize: 2 })
        const mySites = data.elements

        if (mySites.length === 1) {
          console.log('### go directly to site')
          serviceNavigate(`/staging/stagingarea?siteId=${mySites[0].fsiId}`)
        }
      } else if (userRole === constants.user.roles.ETMF_STUDY_USER) {
        console.log('### getting my environment')
        const data: EnvironmentsResponse = await etmfApi.getMyEnvironments({ pageSize: 2 })
        const myEnvs = data.elements

        if (myEnvs.length === 1) {
          console.log('### go directly to environment')
          serviceNavigate(`/staging/stagingarea?environmentId=${myEnvs[0].fsiId}`)
        }
      }
    } catch (error) {
      console.error('Error in redirectUserToOnlySiteStudyWhenCheckmated:', error)
    }
  }

  /**
   * Get current features info (synchronous)
   */
  public getFeaturesInfo(): FeaturesInfo {
    return this.featuresInfo
  }

  /**
   * Load features info from API (async)
   */
  public async loadFeaturesInfo(): Promise<FeaturesInfo> {
    if (this.featuresInfoPromise) {
      return this.featuresInfoPromise
    }

    this.featuresInfoPromise = this.performLoadFeaturesInfo()
    return this.featuresInfoPromise
  }

  private async performLoadFeaturesInfo(): Promise<FeaturesInfo> {
    try {
      await this.waitForCheckmate()

      // Load all features in parallel
      const [etmfFeature, rcrFeature, eosFeature, appStatus, allFeatures] = await Promise.allSettled([
        settingsApi.getEtmfFeature(),
        settingsApi.getRcrFeature(),
        settingsApi.getEosFeature(),
        settingsApi.appStatus(),
        settingsApi.getAllFeatures()
      ])

      // Update ETMF feature
      if (etmfFeature.status === 'fulfilled') {
        this.featuresInfo.etmfFeature.show = etmfFeature.value === true
      }

      // Update RCR feature
      if (rcrFeature.status === 'fulfilled') {
        this.featuresInfo.rcrFeature.show = rcrFeature.value === true
      }

      // Update EOS feature
      if (eosFeature.status === 'fulfilled') {
        this.featuresInfo.eosFeature.show = eosFeature.value === true
      }

      // Update app status data
      if (appStatus.status === 'fulfilled') {
        const data = appStatus.value as AppStatusResponse

        if (data?.qcReviewMaxDoc && data?.qcReviewEnabled) {
          this.featuresInfo.qcReview = {
            qcReviewMaxDoc: data.qcReviewMaxDoc,
            qcReviewEnabled: data.qcReviewEnabled
          }
        }

        if (data?.eosWorkflowDraftCreationPollingInterval) {
          this.featuresInfo.eosPollingInterval = data.eosWorkflowDraftCreationPollingInterval
        }

        if (data?.enableDocDetailsExpirationDate) {
          this.featuresInfo.isEnableDocDetailsExpirationDate = data.enableDocDetailsExpirationDate
        }

        this.featuresInfo.eventTriggers = data?.eventTriggers || ''
      }

      // Update all features data
      if (allFeatures.status === 'fulfilled') {
        this.featuresInfo.allFeaturesData =
          typeof allFeatures.value === 'object' && allFeatures.value !== null
            ? { ...(allFeatures.value as Record<string, any>) }
            : {}
      } else if (allFeatures.status === 'rejected') {
        const error = allFeatures.reason
        if (error?.data?.message) {
          toast.error(error.data.message)
        } else {
          toast.error(translateService.t('ERROR'))
        }
      }

      // Handle unblinding lineage update
      const cSelectionParams = checkmateService.getCSelectionsParams()
      const cdsUuid = cSelectionParams?.['com:mdsol:client_division_schemes']

      if (cdsUuid) {
        try {
          const result = await appsApiService.updateUnblindingLineage(cdsUuid)
          console.log('Unblinding lineage update success:', result)
        } catch (err) {
          console.error('Unblinding lineage update fail:', err)
        }
      }

      return this.featuresInfo
    } catch (error) {
      console.error('Error loading features info:', error)
      return this.featuresInfo
    }
  }

  /**
   * Get current allowed auth types (synchronous)
   */
  public getAllowedAuthTypes(): AllowedAuthTypes {
    return this.allowedAuthTypes
  }

  /**
   * Load allowed auth types from API (async)
   */
  public async loadAllowedAuthTypes(): Promise<AllowedAuthTypes> {
    if (this.allowedAuthTypesPromise) {
      return this.allowedAuthTypesPromise
    }

    this.allowedAuthTypesPromise = this.performLoadAllowedAuthTypes()
    return this.allowedAuthTypesPromise
  }

  private async performLoadAllowedAuthTypes(): Promise<AllowedAuthTypes> {
    try {
      const resp = await settingsApi.allowedAuthTypes()
      this.allowedAuthTypes.allowedAuthTypes = Array.isArray(resp) ? resp : []
      return this.allowedAuthTypes
    } catch (error) {
      console.error('Error loading allowed auth types:', error)
      return this.allowedAuthTypes
    }
  }

  /**
   * Wait for Checkmate initialization
   */
  public async waitForCheckmate(): Promise<boolean> {
    if (this.checkmatePromise) {
      return this.checkmatePromise
    }

    this.checkmatePromise = this.performWaitForCheckmate()
    return this.checkmatePromise
  }

  private async performWaitForCheckmate(): Promise<boolean> {
    return new Promise((resolve) => {
      if (checkmateService.isCheckmated()) {
        console.log('### app is checkmated')

        // Wait for cSelections to be ready before proceeding (blocking until loaded or timeout)
        // Legacy pattern: resolve the promise as soon as c_selections are ready, then fire DRCM
        // as a side-effect (fire-and-forget) — NOT blocking on DRCM connection.
        const waitForCSelections = (iteration: number) => {
          if (checkmateService.getCSelections() !== '') {
            console.log('### checking cselections - loaded', checkmateService.getCSelectionsParams())
            checkmateService.onCheckmateInit()
            // Fire-and-forget DRCM connection — matches legacy: def.promise.then(() => initializeConnectionWithDirectRCM())
            checkmateService
              .initializeConnectionWithDirectRCM()
              .then(() => console.log('### Direct RCM connection initialized successfully'))
              .catch((error) => console.error('### Failed to initialize Direct RCM connection:', error))
            // Resolve immediately, do NOT wait for DRCM (legacy behavior)
            resolve(true)
          } else {
            if (iteration < 100) {
              setTimeout(() => {
                waitForCSelections(iteration + 1)
              }, 100)
            } else {
              console.log('### checking cselections - unavailable')
              // Still call onCheckmateInit and resolve, as in legacy
              checkmateService.onCheckmateInit()
              resolve(true)
            }
          }
        }
        waitForCSelections(0)
      } else {
        console.log('### app is not checkmated')
        resolve(true)
      }
    })
  }

  private async checkCSelectionsAreReady(iteration: number): Promise<void> {
    return new Promise((resolve) => {
      if (checkmateService.getCSelections() !== '') {
        console.log('### checking cselections - loaded', checkmateService.getCSelectionsParams())
        resolve()
      } else {
        console.log('### checking cselections - failed', iteration)
        if (iteration < 100) {
          setTimeout(() => {
            this.checkCSelectionsAreReady(iteration + 1).then(resolve)
          }, 100)
        } else {
          console.log('### checking cselections - unavailable')
          resolve()
        }
      }
    })
  }

  /**
   * Check if translations are ready
   */
  public async checkTranslationReady(): Promise<boolean> {
    return new Promise((resolve) => {
      translateService.onReady(() => {
        resolve(true)
      })
    })
  }

  /**
   * Check if user is logged in
   */
  public async checkLogin(updateUserData: boolean = false, doRetries: boolean = false): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (authService.isLoggedIn()) {
        this.performCheckLogin(updateUserData, doRetries ? 0 : -1, resolve, reject)
      } else {
        this.redirectToLogin(reject)
      }
    })
  }

  private async performCheckLogin(
    updateUserData: boolean,
    repeat: number,
    resolve: (value: boolean) => void,
    reject: (reason: any) => void
  ): Promise<void> {
    try {
      await authApiService.validateToken()

      if (updateUserData) {
        authService.getCurrentUserData()
      }

      resolve(true)
    } catch (error: any) {
      if (error?.status === 401) {
        this.redirectToLogin(reject)
      } else {
        this.retryCheckLogin(updateUserData, repeat, resolve, reject)
      }
    }
  }

  private retryCheckLogin(
    updateUserData: boolean,
    repeat: number,
    resolve: (value: boolean) => void,
    reject: (reason: any) => void
  ): void {
    if (repeat < 10 && repeat !== -1) {
      repeat++
      console.error('Session check failed. Retry: ' + repeat)
      setTimeout(() => {
        this.performCheckLogin(updateUserData, repeat, resolve, reject)
      }, 5000)
    } else {
      this.redirectToLogin(reject)
    }
  }

  private redirectToLogin(reject: (reason: any) => void): void {
    console.error('Session check failed. Redirecting to login.')
    reject('login')
    this.redirectToLoginPage()
  }

  private lastRedirectTime = 0

  /**
   * Redirect to login page (throttled)
   */
  public redirectToLoginPage(toParams?: any): void {
    const now = Date.now()
    if (now - this.lastRedirectTime < 500) {
      return // Throttle redirects
    }
    this.lastRedirectTime = now

    if (window.location.pathname !== '/login') {
      console.log('redirecting to login', toParams)

      // Build query params: include referrer and current page as target
      const queryParams = new URLSearchParams()
      if (toParams?.referrer === 'email') {
        queryParams.set('referrer', 'email')
      }
      // Pass current URL as target for post-login redirect
      const currentPage = window.location.pathname + window.location.search
      queryParams.set('target', currentPage)

      const params = queryParams.toString() ? `?${queryParams.toString()}` : ''
      console.log('performing redirect to login', params)
      window.location.href = `/login${params}`
    }
  }
}

// Export singleton instance
export const initService = InitService.getInstance()

// Extend window interface for global properties
declare global {
  interface Window {
    pendo?: {
      initialize: (config: PendoConfig) => void
    }
    trackActivity?: {
      fromCookies: (config: any) => CasActivitySettings
      (settings: CasActivitySettings): any
    }
    tracker?: any
  }
}
